import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import * as Compare from '@Queries/Compare';
import * as ApiFavorites from '@Api/Favorites';
import { isBrowser } from '@Utils/checkRuntime';
import generateQueryKeys from '@Queries/helpers/generateQueryKeys';
import useNavigation from '@Navigation/useNavigation';
import { useRequest } from '@Contexts/Request';

import type { UseInfiniteQueryResult, InfiniteData } from '@tanstack/react-query';
import type { FavoritesData } from '@Types/Favorites';

export type Result = UseInfiniteQueryResult<InfiniteData<FavoritesData>>;

const useInfiniteFavorites = (): Result => {
  const { region } = useRequest();
  const { pathname, search } = useNavigation();
  const matches = pathname.match(/.*page-([0-9]{0,})$/);
  const page = Number(matches ? matches[1] : 1);
  const compareList = Compare.useCompareList();

  const productsId = useMemo(() => {
    if (compareList.isPending) return [];

    return compareList.data?.products.map((product) => product.id) || [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const keys = generateQueryKeys({
    keys: ['infiniteFavorites', region, productsId.join()],
    pathname,
    search,
  });

  const category = useInfiniteQuery({
    queryKey: keys,
    queryFn: ({ pageParam }) => {
      return ApiFavorites.getFavoritesProducts({
        page: pageParam || page,
        search,
      });
    },
    initialPageParam: 0,
    enabled: isBrowser,
    placeholderData: (previousData) => previousData,
    getNextPageParam: (lastPage) => {
      if (!lastPage) return null;

      const nextPageParam = lastPage.page + 1;

      return nextPageParam > lastPage.pageCount ? null : nextPageParam;
    },
  });

  return category;
};

export default useInfiniteFavorites;
